import { HttpErrors } from '@omecare/utils';
import { AlertCircle, TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import ImportAccountingDoctorAttendingCsvForm from '@/components/forms/import-accounting-doctor-attending-csv-form/import-accounting-doctor-attending-csv-form';
import { useImportAccountingDoctorAttendingCsvForm } from '@/components/forms/import-accounting-doctor-attending-csv-form/use-import-accounting-doctor-attending-csv-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import useAccountingImportDoctolibCsvDazzMutation from '@/hooks/mutations/use-accounting-import-doctolib-csv-dazz';
import { TONES } from '@/lib/tones';
import { cn } from '@/lib/utils';
import {
  type AccountingErrorSlot,
  formatAccountingErrorSlot,
  getAccountingErrorSlots,
} from '@/utils/errors/get-error-toast-params';
import { popModal } from '..';
import {
  buildManualReviewState,
  computeWarning,
  findInvoicesImpactedBySlots,
  type HalfDayChoice,
  isOutsideSyncWindow,
  isSubmitDisabled,
  type ManualReviewItem,
  type ManualReviewState,
} from './import-accounting-doctor-attending-csv-dazz-modal.viewmodel';

export type ImportAccountingDoctorAttendingCsvDazzModalApi = {
  onSuccess?: () => void;
  onClose?: () => void;
  onCancel?: () => void;
};

export const ImportAccountingDoctorAttendingCsvDazzModal = ({
  onSuccess,
  onClose,
  onCancel,
}: ImportAccountingDoctorAttendingCsvDazzModalApi) => {
  const { t } = useTranslation();

  const { importAccountingDoctorAttendingCsvForm: form } =
    useImportAccountingDoctorAttendingCsvForm();
  const mutation = useAccountingImportDoctolibCsvDazzMutation();

  const [step, setStep] = useState<'upload' | 'review'>('upload');
  const [reviewItems, setReviewItems] = useState<ManualReviewItem[]>([]);
  const [reviewState, setReviewState] = useState<ManualReviewState>({});
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingMeta, setPendingMeta] = useState<{
    officeId: string;
    doctorAttendingId: string;
  } | null>(null);
  const [offendingSlots, setOffendingSlots] = useState<AccountingErrorSlot[]>(
    [],
  );

  const impactedInvoices = findInvoicesImpactedBySlots({
    items: reviewItems,
    slots: offendingSlots,
  });

  const close = () => {
    popModal('ImportAccountingDoctorAttendingCsvDazzModal');
    onClose?.();
  };

  const cancel = () => {
    popModal('ImportAccountingDoctorAttendingCsvDazzModal');
    onCancel?.();
  };

  const onUploadSubmit = form.handleSubmit(async (values) => {
    let result: Awaited<ReturnType<typeof mutation.mutateAsync>>;
    try {
      setOffendingSlots([]);
      result = await mutation.mutateAsync({
        officeId: values.officeId,
        doctorAttendingId: values.doctorAttending.id,
        doctolibFile: values.doctolibAccountingFile,
      });
    } catch (error) {
      // The toast is raised by the global mutation error handler; keep the
      // modal open and surface the offending slots inline.
      setOffendingSlots(getAccountingErrorSlots(error as HttpErrors));
      return;
    }
    if (result.status === 'success') {
      toast.success(t('import-accounting-dazz.success'));
      onSuccess?.();
      close();
      return;
    }
    setReviewItems(result.items as ManualReviewItem[]);
    setReviewState(buildManualReviewState(result.items as ManualReviewItem[]));
    setPendingFile(values.doctolibAccountingFile);
    setPendingMeta({
      officeId: values.officeId,
      doctorAttendingId: values.doctorAttending.id,
    });
    setStep('review');
  });

  const onReviewSubmit = async () => {
    if (!pendingFile || !pendingMeta) return;
    const resolutions = Object.entries(reviewState)
      .filter((entry): entry is [string, HalfDayChoice] => entry[1] != null)
      .map(([invoiceIdentifier, halfDay]) => ({
        invoiceIdentifier,
        halfDay,
      }));
    let result: Awaited<ReturnType<typeof mutation.mutateAsync>>;
    try {
      setOffendingSlots([]);
      result = await mutation.mutateAsync({
        officeId: pendingMeta.officeId,
        doctorAttendingId: pendingMeta.doctorAttendingId,
        doctolibFile: pendingFile,
        manualResolutions: resolutions,
      });
    } catch (error) {
      // The toast is raised by the global mutation error handler; keep the
      // review open and flag the rows the operator has to fix.
      setOffendingSlots(getAccountingErrorSlots(error as HttpErrors));
      return;
    }
    if (result.status === 'success') {
      toast.success(t('import-accounting-dazz.success'));
      onSuccess?.();
      close();
    } else {
      // Defensive — shouldn't happen because all rows are now resolved
      toast.error(t('import-accounting-dazz.unexpected_review'));
    }
  };

  return (
    <Dialog
      open={true}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      <DialogContent
        className={step === 'review' ? 'sm:max-w-4xl' : 'sm:max-w-2xl'}
        showCloseButton={false}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t('import-accounting-dazz.title')}</DialogTitle>
          {step === 'upload' && (
            <DialogDescription>
              {t('import-accounting-dazz.upload.description')}
            </DialogDescription>
          )}
          {step === 'review' && (
            <DialogDescription>
              {t('import-accounting-dazz.review.description')}
            </DialogDescription>
          )}
        </DialogHeader>

        {offendingSlots.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
            <p className="flex items-center gap-2 font-medium text-destructive text-sm">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {t('import-accounting-dazz.offending_slots.title')}
            </p>
            <ul className="mt-2 list-inside list-disc text-muted-foreground text-sm">
              {offendingSlots.map((slot) => (
                <li key={slot.id}>{formatAccountingErrorSlot({ slot, t })}</li>
              ))}
            </ul>
          </div>
        )}

        {step === 'upload' && (
          <>
            {isOutsideSyncWindow(new Date()) && (
              <p className={cn('mb-3 text-xs', TONES.amber.text)}>
                {t('import-accounting-dazz.upload.sync_window_notice')}
              </p>
            )}
            <ImportAccountingDoctorAttendingCsvForm form={form} />
          </>
        )}

        {step === 'review' && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="py-2">
                  {t('import-accounting-dazz.review.col.invoice')}
                </TableHead>
                <TableHead className="py-2">
                  {t('import-accounting-dazz.review.col.beneficiary')}
                </TableHead>
                <TableHead className="py-2">
                  {t('import-accounting-dazz.review.col.date')}
                </TableHead>
                <TableHead className="py-2">
                  {t('import-accounting-dazz.review.col.amount')}
                </TableHead>
                <TableHead className="py-2">
                  {t('import-accounting-dazz.review.col.half_day')}
                </TableHead>
                <TableHead className="w-10 py-2">
                  <span className="sr-only">
                    {t('import-accounting-dazz.review.col.warning')}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviewItems.map((item) => {
                const choice = reviewState[item.invoiceIdentifier];
                const warning = computeWarning(item, choice);
                const isImpacted = impactedInvoices.has(item.invoiceIdentifier);
                return (
                  <TableRow
                    key={item.invoiceIdentifier}
                    className={cn(
                      isImpacted && 'bg-destructive/10 hover:bg-destructive/15',
                    )}
                  >
                    <TableCell className="py-2 text-sm">
                      {item.invoiceIdentifier}
                    </TableCell>
                    <TableCell className="py-2 text-sm">
                      {item.beneficiary}
                    </TableCell>
                    <TableCell className="py-2 text-sm">{item.date}</TableCell>
                    <TableCell className="py-2 text-sm">
                      {item.totalAmountDue}
                    </TableCell>
                    <TableCell className="py-2 text-sm">
                      <Select
                        value={choice ?? ''}
                        onValueChange={(v) =>
                          setReviewState((prev) => ({
                            ...prev,
                            [item.invoiceIdentifier]: v as HalfDayChoice,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={t(
                              'import-accounting-dazz.review.placeholder.half_day',
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="morning">
                            {t('import-accounting-dazz.review.morning')}
                          </SelectItem>
                          <SelectItem value="afternoon">
                            {t('import-accounting-dazz.review.afternoon')}
                          </SelectItem>
                          <SelectItem value="night">
                            {t('import-accounting-dazz.review.night')}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="w-10 py-2">
                      {isImpacted && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={t(
                                'import-accounting-dazz.offending_slots.row',
                              )}
                              className="inline-flex items-center justify-center"
                            >
                              <AlertCircle className="h-4 w-4 text-destructive" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t('import-accounting-dazz.offending_slots.row')}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {!isImpacted && warning != null && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              aria-label={t(
                                `import-accounting-dazz.review.warning.${warning}`,
                              )}
                              className="inline-flex items-center justify-center"
                            >
                              {warning === 'work_schedule_fallback' ? (
                                <TriangleAlert
                                  className={cn('h-4 w-4', TONES.amber.text)}
                                />
                              ) : (
                                <AlertCircle className="h-4 w-4 text-destructive" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {t(
                              `import-accounting-dazz.review.warning.${warning}`,
                            )}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={cancel}
          >
            {t('btn.cancel')}
          </Button>
          {step === 'upload' && (
            <Button
              onClick={() => onUploadSubmit()}
              loading={mutation.isPending}
            >
              {t('import-accounting-dazz.upload.submit')}
            </Button>
          )}
          {step === 'review' && (
            <Button
              onClick={onReviewSubmit}
              disabled={isSubmitDisabled(reviewState)}
              loading={mutation.isPending}
            >
              {t('import-accounting-dazz.review.submit')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
